// Stage narrations: explain what the AI is doing at each processing stage.
// Each stage has 2-3 variants; the component selects one based on caseId hash.

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Calendar,
  SearchX,
  BookOpen,
  GitCompareArrows,
  MessageSquare,
  Scale,
  FileText,
  Link2,
  Clock,
  ScanSearch,
  Gavel,
  Layers,
  ScrollText,
  HeartPulse,
  Stethoscope,
  Hourglass,
  Target,
  ShieldCheck,
  Sparkles,
  Eye,
  Brain,
  ListChecks,
} from 'lucide-react';

export interface StageNarration {
  headline: string;
  variants: string[];
}

export const stageNarrations: Record<string, StageNarration> = {
  FETCHING_DOCUMENTS: {
    headline: 'Preparing your documents',
    variants: [
      'Securely loading your uploaded records into the analysis pipeline...',
      'Retrieving your clinical records and preparing them for AI analysis...',
      'Assembling your case documents — every page will be examined systematically...',
    ],
  },
  MAP_PROCESSING: {
    headline: 'Reading and extracting findings',
    variants: [
      'Reading each document page by page, identifying clinical events, diagnoses, and treatments...',
      'Analysing each document individually to extract medical events, dates, and clinical details...',
      'Carefully examining your records, extracting key events and tagging them with dates and confidence levels...',
    ],
  },
  REDUCE_PROCESSING: {
    headline: 'Cross-referencing across documents',
    variants: [
      'Comparing findings across all documents — merging duplicates and flagging any date or detail conflicts...',
      'Synthesising findings into a unified view. Duplicate events are being consolidated and conflicts resolved...',
      'Cross-referencing your documents to build a complete picture, reconciling events that appear in multiple records...',
    ],
  },
  PARSING_CITATIONS: {
    headline: 'Verifying citations',
    variants: [
      'Linking every finding back to its source document and page number for full traceability...',
      'Building the citation layer — mapping each event to specific pages and passages in your original documents...',
      'Final verification: ensuring every finding has a clear source reference with standardised terminology...',
    ],
  },
  OBJECTIVE_GENERATION: {
    headline: 'Generating case analyses',
    variants: [
      'Running gap analysis, inconsistency detection, standard of care, causation, and comparative analyses from your extracted evidence...',
      'Analysing your chronology and metadata to identify gaps, inconsistencies, and standard of care issues...',
      'Five specialist analyses are running in parallel — gaps, inconsistencies, standard of care, causation, and comparative review...',
    ],
  },
  COMPLETED: {
    headline: 'Analysis complete',
    variants: [
      'Your records have been fully analysed. Findings, chronology, and source citations are ready for review.',
    ],
  },
};

// Select a narration variant deterministically from a caseId string.
export function pickVariant(caseId: string, variants: string[]): string {
  let hash = 0;
  for (let i = 0; i < caseId.length; i++) {
    hash = (hash * 31 + caseId.charCodeAt(i)) | 0;
  }
  return variants[Math.abs(hash) % variants.length];
}

// Curated tips shown in the carousel during analysis.

export type TipCategory = 'feature' | 'insight' | 'quality';

export interface InsightTip {
  category: TipCategory;
  title: string;
  text: React.ReactNode;
  icon: LucideIcon;
  reference?: string;
}

export const insightTips: InsightTip[] = [
  // Feature tips — teach the product with actionable guidance
  {
    category: 'feature',
    icon: Calendar,
    title: 'Interactive Chronology Timeline',
    text: (
      <>
        Once analysis completes, open the <strong>Chronology tab</strong> to see every clinical event plotted on an interactive timeline. You can filter by event type, date range, or document source to zero in on the periods most relevant to your case. Events are <em>colour-coded by category</em> — diagnoses, procedures, medications, referrals — making it easy to spot patterns at a glance and identify the sequence of consultations and treatments leading up to an adverse outcome.
      </>
    ),
  },
  {
    category: 'feature',
    icon: SearchX,
    title: 'Identifying Gaps in the Record',
    text: (
      <>
        The <strong>Gaps tab</strong> automatically highlights periods where no clinical records exist. Missing records can be critical — they may indicate lost notes, unreported consultations, or periods where the claimant sought care elsewhere. Each gap is displayed with its <em>date range and surrounding context</em>, so you can prepare targeted Part 18 requests or third-party disclosure applications for the specific periods flagged without sifting through the full record set.
      </>
    ),
  },
  {
    category: 'feature',
    icon: BookOpen,
    title: 'Page-Level Citation Traceability',
    text: (
      <>
        Every finding ClinLexis&#8482; extracts is linked to a <strong>specific page and passage</strong> in your source documents. Click any citation to jump directly to the original record and see the surrounding context highlighted. This means you can verify every AI-generated finding against the primary evidence, giving you the confidence to rely on the analysis in advice, reports, and court bundles — <em>without taking anything on trust</em>.
      </>
    ),
  },
  {
    category: 'feature',
    icon: GitCompareArrows,
    title: 'Detecting Inconsistencies Across Records',
    text: (
      <>
        Medical records from different providers frequently contradict each other — conflicting dates of onset, differing accounts of symptoms, or mismatched medication lists. The <strong>Inconsistencies tab</strong> cross-references all documents to surface these conflicts automatically, presenting each one alongside its <em>source citations from both records</em>. Review the flagged inconsistencies to assess which account is more reliable and build a clearer picture of the clinical history.
      </>
    ),
  },
  {
    category: 'feature',
    icon: MessageSquare,
    title: 'Conversational Case Q&A',
    text: (
      <>
        The <strong>Ask panel</strong> lets you query your case in natural language across all analysed documents. Ask questions like <em>"When was the claimant first referred to a specialist?"</em> or <em>"What medications were prescribed after the surgery?"</em> ClinLexis&#8482; searches across every record and returns answers with source citations and page references, saving you from manually re-reading hundreds of pages to locate a single detail.
      </>
    ),
  },
  {
    category: 'feature',
    icon: Scale,
    title: 'Standard of Care Analysis',
    text: (
      <>
        The <strong>Standard of Care tab</strong> evaluates documented treatment against recognised clinical guidelines, including NICE, SIGN, and Royal College standards. Each comparison highlights where care <em>may have fallen below an acceptable standard</em>, complete with the relevant guideline reference. Use this as a structured starting point when instructing medical experts, giving them a focused comparison rather than asking them to review raw records from scratch.
      </>
    ),
  },
  {
    category: 'feature',
    icon: FileText,
    title: 'Exporting Court-Ready Reports',
    text: (
      <>
        From the <strong>Reports tab</strong>, generate a comprehensive chronology as a formatted Word document or PDF. The exported report includes event dates, descriptions, source references, and page numbers in a layout suitable for counsel, experts, and court bundles. You can customise which <em>event categories and date ranges</em> to include before exporting, ensuring the output is tailored to the specific issues in your case.
      </>
    ),
  },
  {
    category: 'feature',
    icon: Link2,
    title: 'Causation Chain Analysis',
    text: (
      <>
        Establishing causation requires tracing a clear chain from the alleged breach to the injury sustained. The <strong>Causation tab</strong> maps these links across your records, highlighting the sequence of clinical decisions, their documented outcomes, and any intervening events. Use this to identify where the <em>chain of causation is strongest</em> — and where additional expert evidence may be needed to bridge evidential gaps.
      </>
    ),
  },

  // Industry insights — evidenced with references
  {
    category: 'insight',
    icon: Clock,
    title: 'Chronologies: The Biggest Time Drain',
    text: (
      <>
        Medical chronologies are consistently cited as the <strong>single most time-consuming task</strong> in clinical negligence case preparation. Surveys of UK clinical negligence solicitors found that preparing chronologies from medical records accounts for a disproportionate share of pre-action costs, often requiring <em>days of paralegal or solicitor time</em> for a single case. ClinLexis&#8482; generates a first-draft chronology in minutes, freeing your team to focus on legal analysis and case strategy rather than data entry.
      </>
    ),
    reference: 'AvMA, "Clinical Negligence: The Costs Challenge" (2023); The Law Society, Clinical Negligence Best Practice Guide',
  },
  {
    category: 'insight',
    icon: ScanSearch,
    title: 'Manual Record Review Misses Critical Events',
    text: (
      <>
        Research in health informatics has shown that manual chart review can fail to identify a significant proportion of adverse events. One landmark study found that traditional review methods detected only around <strong>10–45% of actual adverse events</strong>, compared to structured trigger-tool approaches. ClinLexis&#8482; mitigates this by <em>systematically examining every page</em> and cross-referencing findings across documents, reducing the risk of evidence being overlooked in voluminous record sets.
      </>
    ),
    reference: 'Classen et al., "Global Trigger Tool Shows That Adverse Events in Hospitals May Be Ten Times Greater Than Previously Measured", Health Affairs, 30(4), 581–589 (2011)',
  },
  {
    category: 'insight',
    icon: Gavel,
    title: 'The Bolam and Bolitho Standards',
    text: (
      <>
        In UK clinical negligence law, breach of duty is assessed against the <strong>Bolam standard</strong>: would a responsible body of medical professionals have acted in the same way? The <em>Bolitho refinement</em> adds that the professional opinion relied upon must withstand logical analysis. When reviewing your chronology, consider whether each clinical decision would satisfy both tests. ClinLexis&#8482;'s Standard of Care analysis provides a structured comparison against published guidelines to help frame this assessment.
      </>
    ),
    reference: 'Bolam v Friern Hospital Management Committee [1957] 1 WLR 582; Bolitho v City and Hackney Health Authority [1998] AC 232',
  },
  {
    category: 'insight',
    icon: Layers,
    title: 'The Volume Challenge: 500+ Pages Per Case',
    text: (
      <>
        NHS Resolution data shows that record volumes in clinical negligence claims have increased substantially, with many cases now involving <strong>500 to over 1,000 pages</strong> from multiple healthcare providers. Each trust or GP practice uses different formats, templates, and abbreviations, making manual collation <em>error-prone and laborious</em>. ClinLexis&#8482; normalises these variations automatically, producing a unified chronology regardless of source format or provider conventions.
      </>
    ),
    reference: 'NHS Resolution, Annual Report and Accounts 2022/23; Civil Justice Council, Costs and Funding Report (2023)',
  },
  {
    category: 'insight',
    icon: ScrollText,
    title: 'Pre-Action Protocol Compliance',
    text: (
      <>
        The <strong>Pre-Action Protocol for the Resolution of Clinical Disputes</strong> requires claimants to provide a detailed Letter of Claim setting out the chronology of events and the alleged breaches. Failure to comply can result in <em>costs sanctions</em>. A thorough, well-sourced chronology is therefore not optional — it is a procedural requirement. ClinLexis&#8482; helps you build this systematically, with every event traced back to its source document and page number.
      </>
    ),
    reference: 'Pre-Action Protocol for the Resolution of Clinical Disputes, Civil Procedure Rules Practice Direction',
  },
  {
    category: 'insight',
    icon: HeartPulse,
    title: 'The Montgomery Informed Consent Standard',
    text: (
      <>
        Following <strong>Montgomery v Lanarkshire Health Board</strong>, clinicians have a duty to ensure patients are aware of any <em>material risks</em> involved in proposed treatment, and of reasonable alternatives. "Material" is defined by what a reasonable person in the patient's position would attach significance to. When reviewing consent-related events, look for documentation of risk discussions and alternatives presented. ClinLexis&#8482; flags consent-related entries to help you assess Montgomery compliance.
      </>
    ),
    reference: 'Montgomery v Lanarkshire Health Board [2015] UKSC 11',
  },
  {
    category: 'insight',
    icon: Stethoscope,
    title: 'Delayed Diagnosis: The Most Common Allegation',
    text: (
      <>
        NHS Resolution reports that <strong>delayed diagnosis</strong> — particularly of cancer, cardiac conditions, and orthopaedic injuries — remains the most frequent allegation in clinical negligence claims in England. Proving delay requires demonstrating both that an earlier diagnosis was achievable and that earlier intervention would have led to a <em>materially better outcome</em>. ClinLexis&#8482;'s chronology maps symptoms, investigations, and referrals over time, making it easier to identify where diagnostic opportunities may have been missed.
      </>
    ),
    reference: 'NHS Resolution, Annual Report and Accounts 2022/23; Mackenzie v Alcoa Manufacturing (GB) Ltd [2019]',
  },
  {
    category: 'insight',
    icon: Hourglass,
    title: 'Limitation Periods in Clinical Negligence',
    text: (
      <>
        The standard limitation period for clinical negligence claims in England and Wales is <strong>three years</strong> from the date of injury or the <em>"date of knowledge"</em> — whichever is later. In complex cases, identifying the date of knowledge can itself be a significant exercise requiring careful review of the medical records. ClinLexis&#8482;'s chronology helps pinpoint when key information was first communicated to the patient, which is often critical to establishing the limitation start date.
      </>
    ),
    reference: 'Limitation Act 1980, ss. 11 and 14; AB v Ministry of Defence [2012] UKSC 9',
  },

  // Quality & trust — detailed explanations
  {
    category: 'quality',
    icon: Target,
    title: 'Confidence Scoring for Every Finding',
    text: (
      <>
        ClinLexis&#8482; assigns a <strong>confidence score</strong> to each extracted event based on factors including the clarity of the source text, whether dates are explicit or inferred, and whether the finding is corroborated across multiple documents. Lower-confidence findings are <em>clearly flagged</em> so you can prioritise manual verification where it matters most, rather than re-reading the entire record set to check everything.
      </>
    ),
  },
  {
    category: 'quality',
    icon: ShieldCheck,
    title: 'UK-Hosted, Encrypted Data Processing',
    text: (
      <>
        Your documents are processed entirely within AWS's <strong>London (eu-west-2) region</strong>. Data is encrypted with AES-256 at rest and TLS 1.2+ in transit. ClinLexis&#8482; does not share your documents with any third-party services, and the AI models run within the same AWS region. This architecture is designed to meet the <em>data residency and security requirements</em> expected of UK legal and healthcare data.
      </>
    ),
  },
  {
    category: 'quality',
    icon: Sparkles,
    title: 'Automated Cross-Referencing and Deduplication',
    text: (
      <>
        Clinical records frequently describe the same event in multiple places — a hospital discharge summary may repeat findings from the admission notes, or a GP record may reference a specialist letter. ClinLexis&#8482; automatically identifies these <strong>overlapping entries</strong>, merges duplicates, and flags any conflicts in dates or descriptions. This ensures your final chronology is <em>clean and non-repetitive</em> without losing important nuances from different clinical perspectives.
      </>
    ),
  },
  {
    category: 'quality',
    icon: Eye,
    title: 'Full Source Traceability',
    text: (
      <>
        Every piece of AI-generated analysis in ClinLexis&#8482; — from chronology events to standard of care findings — includes a reference back to the <strong>specific document and page number</strong> in the original records. This <em>"show your working"</em> approach means you never have to take the AI's output on trust. Click any citation to view the source passage and verify the finding before relying on it in your work product.
      </>
    ),
  },
  {
    category: 'quality',
    icon: Brain,
    title: 'Multi-Model Validation Pipeline',
    text: (
      <>
        ClinLexis&#8482; uses a <strong>multi-pass analysis pipeline</strong>. A primary large language model extracts clinical events from each document, and a separate model validates, cross-references, and reconciles findings across the full record set. This layered approach <em>reduces the risk of hallucination</em> and improves extraction accuracy compared to a single-pass system, giving you greater confidence in the output.
      </>
    ),
  },
  {
    category: 'quality',
    icon: ListChecks,
    title: 'Standardised Medical Terminology',
    text: (
      <>
        Medical records use a wide variety of abbreviations, acronyms, and shorthand — <em>"SOB"</em> (shortness of breath), <em>"NAD"</em> (no abnormality detected), <em>"Hx"</em> (history), among hundreds of others. ClinLexis&#8482; <strong>normalises these abbreviations</strong> into full clinical terms in the chronology output while preserving the original text in the citation, so both lawyers and medical experts can work from the same document without ambiguity.
      </>
    ),
  },
];

// Shuffle tips deterministically based on caseId so different cases show different orders.
export function shuffleTips(caseId: string): InsightTip[] {
  // Interleave categories first: feature, insight, quality, feature, ...
  const byCategory: Record<TipCategory, InsightTip[]> = { feature: [], insight: [], quality: [] };
  for (const tip of insightTips) {
    byCategory[tip.category].push(tip);
  }
  const interleaved: InsightTip[] = [];
  const categories: TipCategory[] = ['feature', 'insight', 'quality'];
  const maxLen = Math.max(byCategory.feature.length, byCategory.insight.length, byCategory.quality.length);
  for (let i = 0; i < maxLen; i++) {
    for (const cat of categories) {
      if (i < byCategory[cat].length) {
        interleaved.push(byCategory[cat][i]);
      }
    }
  }

  // Fisher-Yates shuffle with deterministic seed from caseId
  const arr = [...interleaved];
  let seed = 0;
  for (let i = 0; i < caseId.length; i++) {
    seed = (seed * 31 + caseId.charCodeAt(i)) | 0;
  }
  const nextRand = () => {
    seed = (seed * 1103515245 + 12345) | 0;
    return (seed >>> 16) & 0x7fff;
  };
  for (let i = arr.length - 1; i > 0; i--) {
    const j = nextRand() % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// --- Duration Estimation ---

export interface DurationEstimate {
  lowMinutes: number;
  highMinutes: number;
}

export function estimateDurationRange(
  totalPages: number,
  documentCount: number,
): DurationEstimate {
  // Hybrid OCR (~8-12s/page), Haiku windowed reduce (~2-5s/window)
  const baseSecondsLow = 90;
  const baseSecondsHigh = 150;

  const perPageLow = 8;
  const perPageHigh = 12;

  // Windowed reduce is parallel so overhead is bounded, not per-doc
  const reduceOverheadLow = Math.min(documentCount, 6) * 8;
  const reduceOverheadHigh = Math.min(documentCount, 6) * 15;

  const totalLow = baseSecondsLow + totalPages * perPageLow + reduceOverheadLow;
  const totalHigh = baseSecondsHigh + totalPages * perPageHigh + reduceOverheadHigh;

  const lowMinutes = Math.max(1, Math.ceil(totalLow / 60));
  const highMinutes = Math.max(lowMinutes + 1, Math.ceil(totalHigh / 60));

  return { lowMinutes, highMinutes };
}

export function formatDurationRange(lowMinutes: number, highMinutes: number): string {
  if (highMinutes <= 2) return '1\u20132 min';
  if (lowMinutes >= 60) {
    const lowHrs = Math.floor(lowMinutes / 60);
    const highHrs = Math.ceil(highMinutes / 60);
    if (lowHrs === highHrs) return `${lowHrs} hr`;
    return `${lowHrs}\u2013${highHrs} hr`;
  }
  if (lowMinutes >= 10) {
    const roundedLow = Math.floor(lowMinutes / 5) * 5;
    const roundedHigh = Math.ceil(highMinutes / 5) * 5;
    return `${roundedLow}\u2013${roundedHigh} min`;
  }
  return `${lowMinutes}\u2013${highMinutes} min`;
}

export function getDurationExplanation(): string {
  return 'Each page is carefully read by AI to identify medical events, dates, and findings. ' +
    'Results are then cross-referenced across all your documents to build a complete, unified picture ' +
    '\u2014 similar to a medical expert reviewing a case file, but checking every detail on every page.';
}

/** @deprecated Use getDurationExplanation() instead */
export const DURATION_EXPLANATION =
  'Each page is carefully read by AI to identify medical events, dates, and findings. ' +
  'Results are then cross-referenced across all your documents to build a complete, unified picture ' +
  '\u2014 similar to a medical expert reviewing a case file, but checking every detail on every page.';

export const DURATION_DISCLAIMER = 'Times may vary with document complexity and length.';

// --- Stage Pipeline Definitions ---

export interface PipelineStage {
  name: string;
  description: string;
  completedSummary?: (ctx: { documentCount: number }) => string;
}

export const PIPELINE_STAGES: PipelineStage[] = [
  {
    name: 'Preparing your documents',
    description: 'Detecting scanned and handwritten pages, running optical character recognition where needed, and extracting machine-readable text from every page of every document \u2014 regardless of format, orientation, or image quality.',
    completedSummary: (ctx) => `Text extracted from all ${ctx.documentCount} document${ctx.documentCount !== 1 ? 's' : ''}`,
  },
  {
    name: 'Reading each document individually',
    description: 'A dedicated AI model is analysing each document in parallel \u2014 reading every page, identifying clinical events, diagnoses, procedures, medications, dates, and treating clinicians. Each finding is tagged with a confidence score and linked to its source page.',
    completedSummary: (ctx) => `${ctx.documentCount} document${ctx.documentCount !== 1 ? 's' : ''} analysed \u00B7 findings extracted from every page`,
  },
  {
    name: 'Cross-referencing & synthesis',
    description: 'Multiple AI models are now working simultaneously across the full record set \u2014 merging duplicate entries, resolving conflicting dates and details between providers, and constructing a unified clinical picture from what are often hundreds of pages of fragmented records.',
    completedSummary: () => 'Findings reconciled across all documents',
  },
  {
    name: 'Verifying citations',
    description: 'Every finding is being traced back to its exact source \u2014 matching each extracted event to a specific document, page, and passage so that nothing in the final output is unsourced. This is the traceability layer that makes the analysis auditable.',
    completedSummary: () => 'All citations verified against source documents',
  },
];

export const STAGE_MAP: Record<string, number> = {
  FETCHING_DOCUMENTS: 0,
  MAP_PROCESSING: 1,
  INTERMEDIATE_REDUCE: 2,
  REDUCE_PROCESSING: 2,
  CHRONOLOGY_EXTRACTION: 2,
  PARSING_CITATIONS: 3,
  NARRATIVE_SYNTHESIS: 3,
  PERSISTING: 3,
  OBJECTIVE_GENERATION: 3,
  COMPLETED: 4,
};

export interface SubCard {
  label: string;
  detail: string;
  activeWhen: string[];
}

export const CROSS_REF_SUB_CARDS: SubCard[] = [
  { label: 'Case summary', detail: 'Merging findings across documents, resolving conflicts', activeWhen: ['REDUCE_PROCESSING', 'INTERMEDIATE_REDUCE'] },
  { label: 'Medical timeline', detail: 'Constructing chronology with temporal reasoning', activeWhen: ['CHRONOLOGY_EXTRACTION'] },
];

/** Phase boundaries on the progress bar — positions must match backend emitProgress weights */
export const PROGRESS_PHASES = [
  { startPct: 0,  label: 'Prepare' },
  { startPct: 10, label: 'Read' },
  { startPct: 40, label: 'Synthesise' },
  { startPct: 72, label: 'Verify' },
] as const;

// --- Pipeline DAG Definitions ---

export type NodeState = 'pending' | 'active' | 'completed' | 'failed';

export interface PipelineNode {
  id: string;
  label: string;
  /** Short description shown below the label in phase cards */
  description: string;
  /** Backend stage keys that mean this node is active */
  activeWhen: string[];
  /** Backend stage keys that mean this node is completed (any reached = done) */
  completedWhen: string[];
  /** SVG position (centre of pill) in viewBox coordinates */
  cx: number;
  cy: number;
}

export interface PipelineEdge {
  from: string;
  to: string;
  /** SVG path d attribute (cubic bezier) */
  d: string;
}

// Layout: viewBox 0 14 680 150
// Intelligence sits on the main line; Chronicle branches below.
const Y_MAIN = 44;
const Y_LOWER = 104;
const Y_VISION = 136;

// Node dimensions
const PW = 140; // node width
const PH = 44;  // node height
const HP = PW / 2; // half width (70)

export const PIPELINE_NODES: PipelineNode[] = [
  {
    id: 'ingest',
    label: 'Ingest',
    description: 'OCR & text extraction',
    activeWhen: ['FETCHING_DOCUMENTS'],
    completedWhen: ['MAP_PROCESSING', 'INTERMEDIATE_REDUCE', 'REDUCE_PROCESSING', 'PARSING_CITATIONS', 'COMPLETED'],
    cx: 75, cy: Y_MAIN,
  },
  {
    id: 'deep-read',
    label: 'Review',
    description: 'AI reviews each document',
    activeWhen: ['MAP_PROCESSING'],
    completedWhen: ['INTERMEDIATE_REDUCE', 'REDUCE_PROCESSING', 'PARSING_CITATIONS', 'COMPLETED'],
    cx: 245, cy: Y_MAIN,
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    description: 'Cross-referencing findings',
    activeWhen: ['INTERMEDIATE_REDUCE', 'REDUCE_PROCESSING'],
    completedWhen: ['NARRATIVE_SYNTHESIS', 'PERSISTING', 'OBJECTIVE_GENERATION', 'COMPLETED'],
    cx: 420, cy: Y_MAIN,
  },
  {
    id: 'chronicle',
    label: 'Chronicle',
    description: 'Building medical timeline',
    activeWhen: ['CHRONOLOGY_EXTRACTION'],
    completedWhen: ['PARSING_CITATIONS', 'NARRATIVE_SYNTHESIS', 'PERSISTING', 'OBJECTIVE_GENERATION', 'COMPLETED'],
    cx: 420, cy: Y_LOWER,
  },
  {
    id: 'vision',
    label: 'Vision',
    description: 'Analysing medical images',
    activeWhen: ['FETCHING_DOCUMENTS', 'MAP_PROCESSING'],
    completedWhen: ['INTERMEDIATE_REDUCE', 'REDUCE_PROCESSING', 'PARSING_CITATIONS', 'COMPLETED'],
    cx: 245, cy: Y_VISION,
  },
  {
    id: 'cite',
    label: 'Cite',
    description: 'Verifying source citations',
    activeWhen: ['PARSING_CITATIONS', 'NARRATIVE_SYNTHESIS', 'PERSISTING'],
    completedWhen: ['OBJECTIVE_GENERATION', 'COMPLETED'],
    cx: 600, cy: Y_MAIN,
  },
  {
    id: 'analyses',
    label: 'Analyses',
    description: 'Generating case analyses',
    activeWhen: ['OBJECTIVE_GENERATION'],
    completedWhen: ['COMPLETED'],
    cx: 775, cy: Y_MAIN,
  },
];

// Orthogonal path helper — straight lines with rounded 90° turns
function orthoPath(x1: number, y1: number, x2: number, y2: number, r = 6): string {
  if (y1 === y2) return `M ${x1} ${y1} H ${x2}`;
  const mx = Math.round((x1 + x2) / 2);
  const dy = y2 > y1 ? 1 : -1;
  return `M ${x1} ${y1} H ${mx - r} Q ${mx} ${y1}, ${mx} ${y1 + dy * r} V ${y2 - dy * r} Q ${mx} ${y2}, ${mx + r} ${y2} H ${x2}`;
}

// Edge paths — orthogonal routing with rounded corners
export const PIPELINE_EDGES: PipelineEdge[] = [
  { from: 'ingest', to: 'deep-read',
    d: orthoPath(75 + HP, Y_MAIN, 245 - HP, Y_MAIN) },
  { from: 'deep-read', to: 'intelligence',
    d: orthoPath(245 + HP, Y_MAIN, 420 - HP, Y_MAIN) },
  { from: 'deep-read', to: 'chronicle',
    d: orthoPath(245 + HP, Y_MAIN, 420 - HP, Y_LOWER) },
  { from: 'ingest', to: 'vision',
    d: orthoPath(75 + HP, Y_MAIN, 245 - HP, Y_VISION) },
  { from: 'intelligence', to: 'cite',
    d: orthoPath(420 + HP, Y_MAIN, 600 - HP, Y_MAIN) },
  { from: 'chronicle', to: 'cite',
    d: orthoPath(420 + HP, Y_LOWER, 600 - HP, Y_MAIN) },
  { from: 'cite', to: 'analyses',
    d: orthoPath(600 + HP, Y_MAIN, 775 - HP, Y_MAIN) },
];

/** Derive node state from current backend stage */
export function getNodeState(
  node: PipelineNode,
  currentStage: string | undefined,
  isComplete: boolean,
  isFailed: boolean,
): NodeState {
  if (isFailed) {
    // If the pipeline failed while this node was active, mark it failed
    if (currentStage && node.activeWhen.includes(currentStage)) return 'failed';
    // Nodes that completed before the failure stay completed
    if (currentStage && node.completedWhen.includes(currentStage)) return 'completed';
    // Everything after the failure stays pending
    return 'pending';
  }
  if (isComplete) return 'completed';
  if (!currentStage) return 'pending';
  if (node.completedWhen.includes(currentStage)) return 'completed';
  if (node.activeWhen.includes(currentStage)) return 'active';
  return 'pending';
}

/** Derive edge state from its endpoint node states */
export function getEdgeState(
  fromState: NodeState,
  toState: NodeState,
): NodeState {
  if (toState === 'completed' || toState === 'failed') return fromState === 'failed' ? 'failed' : 'completed';
  if (toState === 'active') return 'active';
  if (fromState === 'completed' || fromState === 'active') return 'active';
  return 'pending';
}

/**
 * Derive node states from the full events array instead of the single top-level stage.
 * This correctly handles parallel pipeline branches (e.g. Intelligence + Chronicle running
 * concurrently) where the backend's single `stage` field flip-flops between them.
 */
export function computeNodeStates(
  events: { stage: string }[],
  isComplete: boolean,
  isFailed: boolean,
): Map<string, NodeState> {
  const states = new Map<string, NodeState>();
  const seenStages = new Set<string>();

  for (const event of events) {
    if (event.stage) seenStages.add(event.stage);
  }

  // Build predecessor map from graph edges
  const predecessors = new Map<string, string[]>();
  for (const edge of PIPELINE_EDGES) {
    const preds = predecessors.get(edge.to) ?? [];
    preds.push(edge.from);
    predecessors.set(edge.to, preds);
  }

  for (const node of PIPELINE_NODES) {
    if (isComplete) {
      states.set(node.id, 'completed');
      continue;
    }

    const hasStarted = node.activeWhen.some(s => seenStages.has(s));
    const hasDone = node.completedWhen.some(s => seenStages.has(s));

    if (isFailed) {
      if (hasDone) {
        states.set(node.id, 'completed');
      } else if (hasStarted) {
        states.set(node.id, 'failed');
      } else {
        states.set(node.id, 'pending');
      }
    } else if (hasDone) {
      states.set(node.id, 'completed');
    } else if (hasStarted) {
      states.set(node.id, 'active');
    } else {
      states.set(node.id, 'pending');
    }
  }

  // Enforce graph-edge dependencies: a node can only be active/completed if all
  // predecessor nodes (per PIPELINE_EDGES) are completed. This prevents
  // parallel backend stages (e.g. chronology running alongside map) from
  // appearing active or completed before their visual predecessors finish.
  // Nodes are defined in roughly topological order so predecessors are resolved first.
  for (const node of PIPELINE_NODES) {
    const st = states.get(node.id);
    if (st === 'active' || st === 'completed') {
      const preds = predecessors.get(node.id) ?? [];
      if (!preds.every(pid => states.get(pid) === 'completed')) {
        states.set(node.id, 'pending');
      }
    }
  }

  return states;
}

// Category display config
export const categoryConfig: Record<TipCategory, { label: string; borderColor: string; textColor: string; badgeBg: string; badgeText: string }> = {
  feature: {
    label: 'Tip',
    borderColor: 'border-l-cyan-400',
    textColor: 'text-cyan-600 dark:text-cyan-400',
    badgeBg: 'bg-cyan-50 dark:bg-cyan-900/20',
    badgeText: 'text-cyan-600 dark:text-cyan-400',
  },
  insight: {
    label: 'Did you know?',
    borderColor: 'border-l-amber-400',
    textColor: 'text-amber-600 dark:text-amber-400',
    badgeBg: 'bg-amber-50 dark:bg-amber-900/20',
    badgeText: 'text-amber-600 dark:text-amber-400',
  },
  quality: {
    label: 'Quality',
    borderColor: 'border-l-green-400',
    textColor: 'text-green-600 dark:text-green-400',
    badgeBg: 'bg-green-50 dark:bg-green-900/20',
    badgeText: 'text-green-600 dark:text-green-400',
  },
};
